// @ts-check
// View de calendário (doc 06, seção 7): qualquer objeto posicionado pelo campo de data da view, em grade mensal de
// domingo a sábado. Até 5 cartões por dia (o resto em "+N"), "+" no dia cria com a data e arrastar um cartão para
// outro dia move a data (mantém a hora num DATE_TIME). Registros sem data não aparecem, como no Twenty.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { fieldsById } from '../../data/metadata-store.js';
import { findManyRecords } from '../../data/rest.js';
import { createLiveRecordList, mutationCounterState, updateRecordOptimistically } from '../../data/record-store.js';
import { FIELD_TYPE } from '../../lib/field-metadata.js';
import { formatMonthLabel, formatNumber } from '../../lib/format.js';
import { formatPreferencesState } from '../../lib/format-preferences.js';
import { RecordChip } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { getViewQuery } from '../views/view-state.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

const VISIBLE_RECORDS_PER_DAY = 5;
const DAYS_PER_WEEK = 7;
const WEEKS_IN_GRID = 6;
const PAGE_SIZE = 200;
// um mês cheio raramente passa disso; acima, o aviso pede um filtro
const MAXIMUM_RECORDS_PER_MONTH = 1000;

/** @param {number} value */
const pad = (value) => String(value).padStart(2, '0');

/** Chave local YYYY-MM-DD de uma data. @param {Date} date */
export const toDayKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Dia do registro no calendário: DATE é uma data pura; DATE_TIME é convertido para o dia local.
 * @param {string | null | undefined} value @param {string} fieldType
 */
export function recordDayKey(value, fieldType) {
  if (!value) return null;
  if (fieldType === FIELD_TYPE.DATE) return String(value).slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : toDayKey(date);
}

/**
 * Primeiro dia da grade (domingo ou segunda da semana do dia 1, conforme a preferência do membro) e os 42 dias exibidos.
 * @param {Date} monthDate @param {number} [startDay]
 */
export function buildMonthGrid(monthDate, startDay = formatPreferencesState.value.calendarStartDay) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - ((first.getDay() - startDay + 7) % 7));
  return Array.from({ length: DAYS_PER_WEEK * WEEKS_IN_GRID }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

/**
 * Novo valor do campo ao soltar o cartão em outro dia: DATE vira o dia; DATE_TIME mantém a hora local.
 * @param {string} previousValue @param {string} targetDayKey @param {string} fieldType
 */
export function shiftDateValue(previousValue, targetDayKey, fieldType) {
  if (fieldType === FIELD_TYPE.DATE) return targetDayKey;
  const previous = new Date(previousValue);
  const [year, month, day] = targetDayKey.split('-').map(Number);
  const next = new Date(previous);
  next.setFullYear(year, month - 1, day);
  return next.toISOString();
}

/**
 * Filtro do intervalo da grade sobre o campo de data, somado ao filtro da view.
 * @param {any} viewFilter @param {import('../../lib/field-metadata.js').FieldMetadata} field @param {Date[]} days
 */
function buildRangeFilter(viewFilter, field, days) {
  const first = days[0];
  const afterLast = new Date(days[days.length - 1]);
  afterLast.setDate(afterLast.getDate() + 1);
  const isDate = field.type === FIELD_TYPE.DATE;
  const range = [
    { field: field.name, operator: 'gte', value: isDate ? toDayKey(first) : first.toISOString() },
    { field: field.name, operator: 'lt', value: isDate ? toDayKey(afterLast) : afterLast.toISOString() },
  ];
  return { and: viewFilter ? [viewFilter, ...range] : range };
}

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, view: import('../../data/metadata-store.js').View,
 *   onTotalCountChange?: (count: number | null) => void, onRequestCreate?: (prefill: Record<string, unknown>) => void }} props
 */
export function RecordCalendar({ objectMetadata, view, onTotalCountChange, onRequestCreate }) {
  const field = view.calendarFieldMetadataId ? fieldsById.value.get(view.calendarFieldMetadataId) : undefined;
  const liveList = useMemo(() => createLiveRecordList(objectMetadata.nameSingular), [objectMetadata.nameSingular, view.id]);
  useEffect(() => () => liveList.dispose(), [liveList]);
  const [monthDate, setMonthDate] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [isLoading, setIsLoading] = useState(true);
  const [isTruncated, setIsTruncated] = useState(false);
  const [expandedDay, setExpandedDay] = useState(/** @type {string | null} */ (null));
  const [dropDay, setDropDay] = useState(/** @type {string | null} */ (null));
  const draggedIdRef = useRef(/** @type {string | null} */ (null));
  const requestIdRef = useRef(0);
  const days = useMemo(() => buildMonthGrid(monthDate), [monthDate.getTime()]);
  const query = getViewQuery(view);
  const records = liveList.records.value;

  useEffect(() => {
    if (!field) return;
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    const filter = buildRangeFilter(query.filter, field, days);
    (async () => {
      /** @type {any[]} */
      const loaded = [];
      let cursor = /** @type {string | null | undefined} */ (undefined);
      let total = 0;
      do {
        const page = await findManyRecords(objectMetadata, { filter, orderBy: [{ field: field.name, direction: 'AscNullsLast' }], limit: PAGE_SIZE, depth: 0, startingAfter: cursor, drilldown: query.drilldown });
        loaded.push(...page.records);
        total = page.totalCount;
        cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
      } while (cursor && loaded.length < MAXIMUM_RECORDS_PER_MONTH);
      return { loaded, total };
    })()
      .then(({ loaded, total }) => {
        if (requestId !== requestIdRef.current) return;
        liveList.records.value = loaded;
        setIsTruncated(total > loaded.length);
        onTotalCountChange?.(total);
      })
      .catch((error) => requestId === requestIdRef.current && showErrorSnackbar(error))
      .finally(() => requestId === requestIdRef.current && setIsLoading(false));
  }, [objectMetadata.id, view.id, field?.id, query.key, monthDate.getTime(), mutationCounterState.value]);

  if (!field) {
    return html`<div class="record-table-empty"><${EmptyState} icon="IconCalendar" title=${t('parity.calendar.noField')} subtitle=${t('parity.calendar.noFieldHint')} /></div>`;
  }

  /** @type {Map<string, any[]>} */
  const recordsByDay = new Map();
  for (const record of records) {
    const key = recordDayKey(record[field.name], field.type);
    if (!key) continue;
    const list = recordsByDay.get(key) ?? [];
    list.push(record);
    recordsByDay.set(key, list);
  }
  const todayKey = toDayKey(new Date());
  const moveMonth = (/** @type {number} */ offset) => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + offset, 1));
  const weekdayFormatter = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' });

  const dropOn = (/** @type {string} */ dayKey) => {
    const recordId = draggedIdRef.current;
    draggedIdRef.current = null;
    setDropDay(null);
    const record = records.find((candidate) => candidate.id === recordId);
    if (!record || recordDayKey(record[field.name], field.type) === dayKey) return;
    const value = shiftDateValue(record[field.name], dayKey, field.type);
    updateRecordOptimistically(objectMetadata, record.id, { [field.name]: value }).catch(() => {});
  };

  const createOn = (/** @type {string} */ dayKey) => {
    const value = field.type === FIELD_TYPE.DATE ? dayKey : `${dayKey}T09:00`;
    onRequestCreate?.({ [field.name]: value });
  };

  return html`<div class="record-calendar" data-testid="record-calendar">
    <div class="record-calendar-top-bar">
      <span class="record-calendar-title">${formatMonthLabel(monthDate, true)}</span>
      <span class="record-calendar-field"><${Icon} name="IconCalendar" size=${14} />${translateMetadataLabel(field.label)}</span>
      ${isTruncated ? html`<span class="record-calendar-warning">${t('parity.calendar.truncated', { count: formatNumber(MAXIMUM_RECORDS_PER_MONTH) })}</span>` : null}
      <div class="record-calendar-navigation">
        <${LightIconButton} icon="IconChevronLeft" ariaLabel=${t('parity.calendar.previous')} onClick=${() => moveMonth(-1)} />
        <${Button} title=${t('parity.calendar.today')} size="small" variant="secondary" onClick=${() => setMonthDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1))} />
        <${LightIconButton} icon="IconChevronRight" ariaLabel=${t('parity.calendar.next')} onClick=${() => moveMonth(1)} />
      </div>
    </div>
    <div class="record-calendar-grid" role="grid">
      ${days.slice(0, DAYS_PER_WEEK).map((day) => html`<div class="record-calendar-weekday" role="columnheader">${weekdayFormatter.format(day).replace('.', '')}</div>`)}
      ${days.map((day) => {
        const dayKey = toDayKey(day);
        const dayRecords = recordsByDay.get(dayKey) ?? [];
        const isExpanded = expandedDay === dayKey;
        const visible = isExpanded ? dayRecords : dayRecords.slice(0, VISIBLE_RECORDS_PER_DAY);
        const isOutside = day.getMonth() !== monthDate.getMonth();
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        return html`<div
          key=${dayKey}
          class="record-calendar-day"
          role="gridcell"
          data-outside=${isOutside || isWeekend ? 'true' : undefined}
          data-drop-target=${dropDay === dayKey ? 'true' : undefined}
          data-day=${dayKey}
          onDragOver=${(/** @type {DragEvent} */ event) => {
            event.preventDefault();
            if (dropDay !== dayKey) setDropDay(dayKey);
          }}
          onDragLeave=${() => dropDay === dayKey && setDropDay(null)}
          onDrop=${(/** @type {DragEvent} */ event) => {
            event.preventDefault();
            dropOn(dayKey);
          }}
        >
          <div class="record-calendar-day-header">
            <span class="record-calendar-day-number" data-today=${dayKey === todayKey ? 'true' : undefined}>${day.getDate()}</span>
            <button type="button" class="record-calendar-add" aria-label=${t('parity.calendar.addOn', { day: dayKey })} onClick=${() => createOn(dayKey)}>
              <${Icon} name="IconPlus" size=${12} />
            </button>
          </div>
          <div class="record-calendar-cards">
            ${isLoading && dayRecords.length === 0 && day.getDate() % 5 === 0 ? html`<${Skeleton} width="80%" height=${12} />` : null}
            ${visible.map((record) => html`<div
              key=${record.id}
              class="record-calendar-card"
              draggable="true"
              onDragStart=${(/** @type {DragEvent} */ event) => {
                draggedIdRef.current = record.id;
                event.dataTransfer?.setData('text/plain', record.id);
              }}
              onDragEnd=${() => setDropDay(null)}
            ><${RecordChip} objectMetadata=${objectMetadata} record=${record} variant="regular" /></div>`)}
            ${dayRecords.length > VISIBLE_RECORDS_PER_DAY
              ? html`<button type="button" class="record-calendar-more" onClick=${() => setExpandedDay(isExpanded ? null : dayKey)}>
                  ${isExpanded ? t('parity.calendar.showLess') : t('parity.calendar.more', { count: dayRecords.length - VISIBLE_RECORDS_PER_DAY })}
                </button>`
              : null}
          </div>
        </div>`;
      })}
    </div>
  </div>`;
}
